import type { ReactElement } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import type { ActivityCall } from '../../lib/activity/use-activity.js';
import { formatCallDuration, formatDateTime } from '../../lib/format.js';
import { cn } from '@tailoring-crm/ui';

/**
 * Calls that happened, newest first, as the server lists them. Read-only by design: a logged call
 * cannot be edited or deleted, anywhere.
 */
export function CallList({ calls }: { calls: readonly ActivityCall[] }): ReactElement {
  const { t } = useTranslation();

  if (calls.length === 0) {
    return (
      <p className="activity-empty rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
        {t('activity.calls.empty')}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {calls.map((call) => {
        const duration = formatCallDuration(call.durationSeconds);
        const Icon = call.direction === 'inbound' ? ArrowDownLeft : ArrowUpRight;

        return (
          <li key={call.id} className="activity-note rounded-lg border border-border p-3">
            <div className="flex items-start gap-3">
              {/* Direction as an arrow rather than a word: "who rang" is read at a glance down a
                  column, and an icon survives being scanned in a way a label does not. */}
              <Icon
                aria-hidden="true"
                className={cn(
                  'mt-0.5 size-4 shrink-0',
                  call.direction === 'inbound' ? 'text-emerald-600' : 'text-muted-foreground',
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <p className="text-sm font-medium">{call.subject}</p>
                  <span className="text-xs text-muted-foreground">
                    {t(`activity.calls.direction.${call.direction}`)}
                  </span>
                  <span aria-hidden="true" className="text-xs text-muted-foreground">
                    ·
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t(`activity.outcome.${call.outcome}`)}
                  </span>
                  {duration ? (
                    <>
                      <span aria-hidden="true" className="text-xs text-muted-foreground">
                        ·
                      </span>
                      <span className="text-xs tabular-nums text-muted-foreground">{duration}</span>
                    </>
                  ) : null}
                </div>

                {call.notes ? (
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-muted-foreground">
                    {call.notes}
                  </p>
                ) : null}

                <p className="mt-1 text-xs text-muted-foreground">
                  {call.actorName ?? t('activity.notes.unknownAuthor')} ·{' '}
                  {formatDateTime(call.occurredAt)}
                </p>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A call as one timeline headline — "Called — We rang them — spoke to them — 2m 10s" — from the
 * timeline row's `outcome` and `detail`. Shared by every timeline that carries calls.
 */
export function callHeadline(
  outcome: string | null,
  detail: Record<string, unknown> | null,
  t: TFunction,
): string {
  const parts = [t('activity.timeline.call')];
  if (typeof detail?.direction === 'string') {
    parts.push(t(`activity.calls.direction.${detail.direction}`));
  }
  if (outcome) parts.push(t(`activity.outcome.${outcome}`));
  const duration =
    typeof detail?.durationSeconds === 'number' ? formatCallDuration(detail.durationSeconds) : null;
  if (duration) parts.push(duration);
  return parts.join(' — ');
}
